import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Translation } from 'primeng/api';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { I18NSera } from './utils/I18NSera';
import { PrimeNG } from 'primeng/config';

@Injectable({
    providedIn: 'root'
})
export class I18nService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private translateSubscription!: Subscription;

    constructor(private readonly config: PrimeNG, private translate: TranslateService) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.translateSubscription) {
            this.translateSubscription.unsubscribe();
        }
    }

    public setTranslationsPrimeNG(): void {
        let keysPrimeNG: string[] = Object.values(I18NSera);
        this.translateSubscription = this.translate
            .get(keysPrimeNG)
            .pipe(takeUntil(this.destroy$))
            .subscribe((keys: Object) => {
                let keyMap: Map<string, string> = new Map(Object.entries(keys).filter(keyValuePair => keyValuePair[0] !== keyValuePair[1]));
                let newConfigPrimeNG: Translation = {
                    startsWith: keyMap.get(I18NSera.startsWith) ?? CONFIG.startsWith,
                    contains: keyMap.get(I18NSera.contains) ?? CONFIG.contains,
                    notContains: keyMap.get(I18NSera.notContains) ?? CONFIG.notContains,
                    endsWith: keyMap.get(I18NSera.endsWith) ?? CONFIG.endsWith,
                    equals: keyMap.get(I18NSera.equals) ?? CONFIG.equals,
                    notEquals: keyMap.get(I18NSera.notEquals) ?? CONFIG.notEquals,
                    noFilter: keyMap.get(I18NSera.noFilter) ?? CONFIG.noFilter,
                    lt: keyMap.get(I18NSera.lt) ?? CONFIG.lt,
                    lte: keyMap.get(I18NSera.lte) ?? CONFIG.lte,
                    gt: keyMap.get(I18NSera.gt) ?? CONFIG.gt,
                    gte: keyMap.get(I18NSera.gte) ?? CONFIG.gte,
                    is: keyMap.get(I18NSera.is) ?? CONFIG.is,
                    isNot: keyMap.get(I18NSera.isNot) ?? CONFIG.isNot,
                    before: keyMap.get(I18NSera.before) ?? CONFIG.before,
                    after: keyMap.get(I18NSera.after) ?? CONFIG.after,
                    dateIs: keyMap.get(I18NSera.dateIs) ?? CONFIG.dateIs,
                    dateIsNot: keyMap.get(I18NSera.dateIsNot) ?? CONFIG.dateIsNot,
                    dateBefore: keyMap.get(I18NSera.dateBefore) ?? CONFIG.dateBefore,
                    dateAfter: keyMap.get(I18NSera.dateAfter) ?? CONFIG.dateAfter,
                    clear: keyMap.get(I18NSera.clear) ?? CONFIG.clear,
                    apply: keyMap.get(I18NSera.apply) ?? CONFIG.apply,
                    matchAll: keyMap.get(I18NSera.matchAll) ?? CONFIG.matchAll,
                    matchAny: keyMap.get(I18NSera.matchAny) ?? CONFIG.matchAny,
                    addRule: keyMap.get(I18NSera.addRule) ?? CONFIG.addRule,
                    removeRule: keyMap.get(I18NSera.removeRule) ?? CONFIG.removeRule,
                    accept: keyMap.get(I18NSera.accept) ?? CONFIG.accept,
                    reject: keyMap.get(I18NSera.reject) ?? CONFIG.reject,
                    choose: keyMap.get(I18NSera.choose) ?? CONFIG.choose,
                    upload: keyMap.get(I18NSera.upload) ?? CONFIG.upload,
                    cancel: keyMap.get(I18NSera.cancel) ?? CONFIG.cancel,
                    dayNames: this.parseValid(keyMap.get(I18NSera.dayNames), 7) ?? CONFIG.dayNames,
                    dayNamesShort: this.parseValid(keyMap.get(I18NSera.dayNamesShort), 7) ?? CONFIG.dayNamesShort,
                    dayNamesMin: this.parseValid(keyMap.get(I18NSera.dayNamesMin), 7) ?? CONFIG.dayNamesMin,
                    monthNames: this.parseValid(keyMap.get(I18NSera.monthNames), 12) ?? CONFIG.monthNames,
                    monthNamesShort: this.parseValid(keyMap.get(I18NSera.monthNamesShort), 12) ?? CONFIG.monthNamesShort,
                    dateFormat: keyMap.get(I18NSera.dateFormat) ?? CONFIG.dateFormat,
                    firstDayOfWeek: 1,
                    today: keyMap.get(I18NSera.today) ?? CONFIG.today,
                    weekHeader: 'Wk',
                    weak: keyMap.get(I18NSera.weak) ?? CONFIG.weak,
                    medium: keyMap.get(I18NSera.medium) ?? CONFIG.medium,
                    strong: keyMap.get(I18NSera.strong) ?? CONFIG.strong,
                    passwordPrompt: keyMap.get(I18NSera.passwordPrompt) ?? CONFIG.passwordPrompt,
                    emptyMessage: keyMap.get(I18NSera.emptyMessage) ?? CONFIG.emptyMessage,
                    emptyFilterMessage: keyMap.get(I18NSera.emptyFilterMessage) ?? CONFIG.emptyFilterMessage
                };

                this.config.setTranslation(newConfigPrimeNG);
            });
    }

    public parseValid(value: string | undefined, sizeValidator: number): string[] | null {
        let valueList: string[] | null = value ? value.split(',') : null;
        if (valueList && valueList.length === sizeValidator) {
            valueList = valueList.map(s => s.trim());
        } else {
            valueList = null;
        }
        return valueList;
    }
}

export const CONFIG: Translation = {
    startsWith: 'Starts with',
    contains: 'Contains',
    notContains: 'Not contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    noFilter: 'No Filter',
    lt: 'Less than',
    lte: 'Less than or equal to',
    gt: 'Greater than',
    gte: 'Greater than or equal to',
    is: 'Is',
    isNot: 'Is not',
    before: 'Before',
    after: 'After',
    dateIs: 'Date is',
    dateIsNot: 'Date is not',
    dateBefore: 'Date is before',
    dateAfter: 'Date is after',
    clear: 'Clear',
    apply: 'Apply',
    matchAll: 'Match All',
    matchAny: 'Match Any',
    addRule: 'Add Rule',
    removeRule: 'Remove Rule',
    accept: 'Yes',
    reject: 'No',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    dateFormat: 'dd.mm.yy',
    firstDayOfWeek: 1,
    today: 'Today',
    weekHeader: 'Wk',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found'
};
